import { useState, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Plus, X, Download } from 'lucide-react';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Anotacao, AnnotationPin } from './AnnotationPin';
import { AnnotationPanel } from './AnnotationPanel';
import { AnnotationForm } from './AnnotationForm';

// O worker do pdf.js vem do próprio bundle, não da unpkg.com. Buscá-lo num CDN
// exigiria abrir script-src/worker-src para um terceiro — justamente o tipo de
// brecha que a CSP nova fecha (RG-05) — e ainda deixava o visualizador do
// Portal do Cliente refém da disponibilidade da unpkg.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PdfAnnotationViewerProps {
  pdfUrl: string;
  anotacoes: Anotacao[];
  onCreateAnnotation: (dados: { pagina: number; posX: number; posY: number; texto: string; tipo: string }) => Promise<void>;
  onReplyAnnotation: (anotacaoId: string, texto: string) => Promise<void>;
  onResolveAnnotation: (anotacaoId: string) => Promise<void>;
  onDownload?: () => void;
  readOnly?: boolean;
}

export function PdfAnnotationViewer({
  pdfUrl,
  anotacoes,
  onCreateAnnotation,
  onReplyAnnotation,
  onResolveAnnotation,
  onDownload,
  readOnly = false,
}: PdfAnnotationViewerProps) {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [annotationMode, setAnnotationMode] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState<{ pagina: number; posX: number; posY: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  const onDocumentLoadError = useCallback((err: Error) => {
    setError('Erro ao carregar PDF: ' + err.message);
  }, []);

  const handlePageClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!annotationMode || readOnly) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const posX = ((e.clientX - rect.left) / rect.width) * 100;
    const posY = ((e.clientY - rect.top) / rect.height) * 100;

    setNewAnnotation({ pagina: currentPage, posX, posY });
    setAnnotationMode(false);
  }, [annotationMode, currentPage, readOnly]);

  const handleCreateAnnotation = async (dados: { pagina: number; posX: number; posY: number; texto: string; tipo: string }) => {
    await onCreateAnnotation(dados);
    setNewAnnotation(null);
  };

  const handlePinClick = (id: string) => {
    setSelectedId(id === selectedId ? null : id);
    // Scroll to annotation in panel
    const el = document.getElementById(`anotacao-${id}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const currentPageAnnotations = anotacoes.filter(a => a.pagina === currentPage);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= numPages) {
      setCurrentPage(page);
      setNewAnnotation(null);
    }
  };

  return (
    <div className="flex h-full bg-gray-100 dark:bg-gray-900">
      {/* PDF Viewer Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 gap-2">
          <div className="flex items-center gap-2">
            {/* Page Navigation */}
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1 text-sm">
              <input
                type="number"
                min={1}
                max={numPages}
                value={currentPage}
                onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                className="w-12 text-center px-1 py-0.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <span className="text-gray-500 dark:text-gray-400">/ {numPages}</span>
            </div>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= numPages} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom */}
            <button onClick={() => setScale(s => Math.max(0.5, s - 0.2))} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-500 w-12 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(3, s + 0.2))} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              <ZoomIn className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-gray-200 dark:bg-gray-600" />

            {/* Annotation mode toggle */}
            {!readOnly && (
              <button
                onClick={() => { setAnnotationMode(!annotationMode); setNewAnnotation(null); }}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  annotationMode
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {annotationMode ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {annotationMode ? 'Cancelar' : 'Anotar'}
              </button>
            )}

            {/* Download */}
            {onDownload && (
              <button
                onClick={onDownload}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Baixar PDF
              </button>
            )}
          </div>
        </div>

        {/* Annotation mode indicator */}
        {annotationMode && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 px-4 py-1.5 text-xs text-blue-700 dark:text-blue-300 text-center">
            Clique no ponto do PDF onde deseja adicionar a anotacao
          </div>
        )}

        {/* PDF Content */}
        <div className="flex-1 overflow-auto flex justify-center py-4">
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              }
            >
              <div ref={pageRef} className="relative inline-block shadow-lg">
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />

                {/* Annotation overlay */}
                <div
                  className={`absolute inset-0 ${annotationMode ? 'cursor-crosshair' : ''}`}
                  onClick={handlePageClick}
                  style={{ pointerEvents: annotationMode ? 'auto' : 'none' }}
                >
                  {/* Existing pins */}
                  {currentPageAnnotations.map((a) => (
                    <div key={a.id} style={{ pointerEvents: 'auto' }}>
                      <AnnotationPin
                        anotacao={a}
                        index={anotacoes.filter(an => an.pagina === currentPage).indexOf(a)}
                        isSelected={selectedId === a.id}
                        onClick={() => handlePinClick(a.id)}
                      />
                    </div>
                  ))}

                  {/* New annotation form */}
                  {newAnnotation && newAnnotation.pagina === currentPage && (
                    <AnnotationForm
                      pagina={newAnnotation.pagina}
                      posX={newAnnotation.posX}
                      posY={newAnnotation.posY}
                      onSubmit={handleCreateAnnotation}
                      onCancel={() => setNewAnnotation(null)}
                    />
                  )}
                </div>
              </div>
            </Document>
          )}
        </div>
      </div>

      {/* Annotation Panel */}
      <AnnotationPanel
        anotacoes={anotacoes}
        selectedId={selectedId}
        currentPage={currentPage}
        onSelect={setSelectedId}
        onReply={onReplyAnnotation}
        onResolve={onResolveAnnotation}
      />
    </div>
  );
}
